import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import { toast } from 'react-toastify';
import "../Package/PackageForm.css"; // Shared CSS with Package forms

const HolidayTermsandCondition = ({ holidayId, packageDetails, onUpdate }) => {
  const { user } = useAuth();
  
  // State Management
  const [lists, setLists] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  
  // -- State for Add/Edit --
  const initialValues = { title: "", description: "" };
  const [values, setValues] = useState(initialValues);
  
  // -- State for Inline Editing --
  const [editingId, setEditingId] = useState(null);

  // -- State for Deletion --
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // 1. Load and Parse Data from Prop
  useEffect(() => {
    // Key from your console log is 'holidayTerms'
    const rawData = packageDetails?.holidayTerms;

    if (rawData) {
        try {
            const parsed = typeof rawData === 'string'
                ? JSON.parse(rawData)
                : rawData;

            // Map to ensure consistent frontend structure
            const mapped = parsed.map((item, index) => ({
                packageTermsAndConditionId: item.packageTermsAndConditionId || item.PackageTermsAndConditionId || `temp-${index}`,
                title: item.title || item.Title || "",
                description: item.description || item.Description || ""
            }));
            
            setLists(mapped);
        } catch (e) {
            console.error("JSON Parse Error", e);
            setLists([]);
        }
    } else {
        setLists([]);
    }
  }, [packageDetails]);

  // --- CORE SAVE LOGIC (Updates Holiday JSON) ---
  const saveHolidayData = async (updatedList) => {
      const formData = new FormData();
      formData.append("holidayId", holidayId);
      // Backend column: 'holidayTerms'
      formData.append("holidayTerms", JSON.stringify(updatedList)); 
      
      if(user?.id) formData.append("userId", user.id);

      try {
          await apiService.update(config.UPDATEHOLIDAY, formData);
          if(onUpdate) {
              onUpdate(); 
          } else {
              setLists(updatedList);
          }
          return true;
      } catch (error) {
          console.error(error);
          toast.error("Failed to update terms");
          return false;
      }
  };

  // --- HANDLER: ADD or UPDATE ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!values.title.trim() || !values.description.trim()) {
        toast.warning("Title and Description are required");
        return;
    }
    setBtnLoading(true);

    let newList = [...lists];

    if (editingId) {
        // Update Existing
        newList = newList.map(item => 
            item.packageTermsAndConditionId === editingId 
            ? { ...item, ...values } 
            : item
        );
    } else {
        // Add New
        const newItem = {
            packageTermsAndConditionId: crypto.randomUUID(),
            title: values.title,
            description: values.description
        };
        newList.push(newItem);
    }

    const success = await saveHolidayData(newList);
    
    if (success) {

        resetForm();
    }
    setBtnLoading(false);
  };

  // --- HANDLER: DELETE ---
  const handleDelete = async () => {
    if (itemToDelete !== null) {
      const newList = lists.filter(x => x.packageTermsAndConditionId !== itemToDelete);
      const success = await saveHolidayData(newList);
      if(success) 
      handleCloseDeleteModal();
    }
  };

  // --- UI HELPERS ---
  const showEditDetails = (item) => {
      setEditingId(item.packageTermsAndConditionId);
      setValues({ title: item.title, description: item.description });
  };

  const resetForm = () => {
      setValues(initialValues);
      setEditingId(null);
      setBtnLoading(false);
  };

  const handleInputChange = (e) => {
      const { name, value } = e.target;
      setValues({ ...values, [name]: value });
  };

  const handleOpenDeleteModal = (id) => { setItemToDelete(id); setShowDeleteModal(true); };
  const handleCloseDeleteModal = () => { setShowDeleteModal(false); setItemToDelete(null); };

  return (
    <div className="PackageTerms-wrapper">
      <div className="PackageTerms-card card">
        <div className="card-body">
          <h5 className="mb-4">Terms & Conditions</h5>

          {/* List of Terms */}
          <div className="PackageTerms-list">
            {lists.length > 0 ? (
              lists.map((item) => (
                <div
                  key={item.packageTermsAndConditionId}
                  className="PackageTerms-list-item"
                >
                  {/* --- INLINE EDIT FORM --- */}
                  {editingId === item.packageTermsAndConditionId ? (
                    <div className="row g-2 align-items-center">
                      <div className="col-md-3">
                        <input
                          type="text"
                          name="title"
                          value={values.title}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Title"
                        />
                      </div>
                      <div className="col-md-7">
                        <input
                          type="text"
                          name="description"
                          value={values.description}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Description"
                        />
                      </div>
                      <div className="col-md-2 d-flex">
                        <button className="btn btn-success me-2" onClick={handleSubmit} disabled={btnLoading}>
                          <i className="ri-check-line"></i>
                        </button>
                        <button className="btn btn-secondary" onClick={resetForm}>
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* --- VIEW MODE --- */
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="flex-grow-1">
                        <strong>{item.title}</strong> - {item.description}
                      </div>
                      <div className="item-actions">
                        <button className="PackageInclude-action-btn edit" onClick={() => showEditDetails(item)}>
                          <i className="ri-pencil-fill"></i>
                        </button>
                        <button className="PackageInclude-action-btn delete" onClick={() => handleOpenDeleteModal(item.packageTermsAndConditionId)}>
                          <i className="ri-delete-bin-fill"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-muted text-center py-3">No terms and conditions added yet. Add one below!</p>
            )}
          </div>

          {/* --- ADD NEW FORM (Only visible when not editing) --- */}
          {!editingId && (
            <div className="PackageTerms-add-form mt-4">
                <div className="row g-2 align-items-center">
                    <div className="col-md-3">
                        <input
                            type="text"
                            name="title"
                            value={values.title}
                            onChange={handleInputChange}
                            className="form-control"
                            placeholder="Title (e.g. Refund Policy)"
                        />
                    </div>
                    <div className="col-md-8">
                         <input
                            type="text"
                            name="description"
                            value={values.description}
                            onChange={handleInputChange}
                            className="form-control"
                            placeholder="Description"
                        />
                    </div>
                    <div className="col-md-1">
                        <button className="btn btn-success w-100" onClick={handleSubmit} disabled={btnLoading}>
                            {btnLoading ? "..." : <i className="ri-check-line"></i>}
                        </button>
                    </div>
                </div>
            </div>
          )}
        </div>
      </div>

      <DeleteAlert
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleDelete={handleDelete}
      />
    </div>
  );
};

export default HolidayTermsandCondition;